// import 'bootstrap/dist/css/bootstrap.min.css';
import React from 'react';
import { hydrate, render } from 'react-dom';
import { Provider } from 'react-redux';
import App from './App';
import './App.scss';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
// import './index.scss';
import reportWebVitals from './reportWebVitals';
import store from './store';

// Initialize PostHog only if there's a valid API key
const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
if (posthogKey && posthogKey.length > 0) {
  try {
    posthog.init(posthogKey, {
      api_host: 'https://us.i.posthog.com',
      autocapture: false, // Disable autocapture to prevent postMessage issues
      capture_pageview: false, // Disable automatic pageview capture
      disable_session_recording: true, // Disable session recording to prevent iframe issues
    });
  } catch (error) {
    console.warn('Failed to initialize PostHog:', error);
  }
}

// --------------------------------------------------------------------------------
// Globally suppress the annoying (and harmless) "ResizeObserver loop completed
// with undelivered notifications" error that Chrome sometimes throws when flex /
// width transitions trigger lots of resize events (frequently with MUI comps).
// It bubbles up as an uncaught error and pollutes the dev overlay, even though it
// has no functional impact.  This listener stops it from propagating.
// --------------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  const resizeObserverErrMsg = 'ResizeObserver loop completed with undelivered notifications.';
  const resizeObserverErrMsg2 = 'ResizeObserver loop limit exceeded';
  const postMessageErrMsg = 'Maximum call stack size exceeded';
  // Some browsers prepend additional text, so we'll match with includes().

  // Enhanced error suppression for ResizeObserver
  window.addEventListener(
    'error',
    (e) => {
      // Check multiple properties where the error message might be
      const errorMessage = e?.message || e?.error?.message || '';
      
      if (typeof errorMessage === 'string') {
        // Suppress ResizeObserver errors (check for both common variants)
        if (errorMessage.includes(resizeObserverErrMsg) || 
            errorMessage.includes(resizeObserverErrMsg2) ||
            errorMessage.includes('ResizeObserver')) {
          e.stopImmediatePropagation();
          e.preventDefault();
          // Optionally, comment the next line in if you still want it in dev console
          // console.debug('Suppressed ResizeObserver loop error');
          return false; // Explicitly return false to prevent further propagation
        }
        
        // Suppress postMessage call stack errors during auth
        if (errorMessage.includes(postMessageErrMsg) || errorMessage.includes('postMessage')) {
          console.warn('PostMessage error caught and suppressed:', errorMessage);
          e.stopImmediatePropagation();
          e.preventDefault();
          // Clear any stuck state
          sessionStorage.removeItem('loginError');
          return false;
        }
      }
    },
    true // capture phase to catch it before the React overlay
  );

  // Additional console.error override to catch ResizeObserver errors in development
  if (process.env.NODE_ENV === 'development') {
    const originalConsoleError = console.error;
    console.error = function(...args) {
      const errorString = args.join(' ');
      // Filter out ResizeObserver errors from console
      if (errorString.includes('ResizeObserver loop completed') || 
          errorString.includes('ResizeObserver loop limit exceeded') ||
          (errorString.includes('ResizeObserver') && errorString.includes('loop'))) {
        // Optionally log it as a debug message instead
        // console.debug('Suppressed ResizeObserver console error');
        return;
      }
      // Call original console.error for all other errors
      originalConsoleError.apply(console, args);
    };
  }

  const autoplayErrMsg = "play() failed because the user didn't interact with the document first.";
  // Globally suppress the common autoplay promise rejection error that occurs when
  // a media element's play() promise rejects because the user hasn't yet interacted
  // with the document. This is harmless but can clutter the console and dev overlay.
  window.addEventListener('unhandledrejection', (e) => {
    // The reason is usually a DOMException with a message matching autoplayErrMsg.
    // Some browsers may pass the message directly as a string.
    const reason: any = (e as PromiseRejectionEvent).reason;
    
    // Also check for ResizeObserver errors in unhandled rejections
    if (reason && (
      (typeof reason === 'string' && (reason.includes('ResizeObserver') || reason.includes(autoplayErrMsg))) ||
      (reason?.message && typeof reason.message === 'string' && 
        (reason.message.includes('ResizeObserver') || reason.message.includes(autoplayErrMsg)))
    )) {
      e.preventDefault();
      // Optionally, comment the next line in if you still want it in dev console
      // console.debug('Suppressed promise rejection error');
      return false;
    }
  });
}

const ReactApp = () => {
  // Only wrap with PostHogProvider if PostHog is properly initialized
  const appContent = (
    <Provider store={store}>
      <App />
    </Provider>
  );

  // Check if PostHog is initialized with a valid token
  if (posthogKey && posthogKey.length > 0) {
    return (
      <PostHogProvider client={posthog}>
        {appContent}
      </PostHogProvider>
    );
  }

  return appContent;
}

const rootElement = document.getElementById("root");
if (rootElement.hasChildNodes()) {
  hydrate( <ReactApp/>, rootElement);
} else {
  render( <ReactApp/>, rootElement);
}
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
